import { DropdownMenu as Radix } from "radix-ui";
import type { ReactElement, ReactNode } from "react";
import { cx } from "../../lib/cx";
import styles from "./DropdownMenu.module.css";

export interface DropdownMenuProps {
  /** A single focusable element that opens the menu. */
  trigger: ReactElement;
  children: ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/** Accessible action menu (Radix): arrow keys, typeahead, Escape, focus return. */
export function DropdownMenu({
  trigger,
  children,
  align = "end",
  side = "bottom",
  open,
  onOpenChange,
  className,
}: DropdownMenuProps) {
  return (
    <Radix.Root open={open} onOpenChange={onOpenChange}>
      <Radix.Trigger asChild>{trigger}</Radix.Trigger>
      <Radix.Portal>
        <Radix.Content
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cx(styles.content, className)}
        >
          {children}
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  );
}

export interface DropdownMenuItemProps {
  children: ReactNode;
  onSelect?: (event: Event) => void;
  icon?: ReactNode;
  /** Keyboard hint shown on the right, e.g. "⌘K". */
  shortcut?: ReactNode;
  tone?: "default" | "danger";
  disabled?: boolean;
}

export function DropdownMenuItem({
  children,
  onSelect,
  icon,
  shortcut,
  tone = "default",
  disabled,
}: DropdownMenuItemProps) {
  return (
    <Radix.Item
      className={cx(styles.item, tone === "danger" && styles.danger)}
      onSelect={onSelect}
      disabled={disabled}
    >
      {icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className={styles.label}>{children}</span>
      {shortcut ? <span className={styles.shortcut}>{shortcut}</span> : null}
    </Radix.Item>
  );
}

export function DropdownMenuLabel({ children }: { children: ReactNode }) {
  return <Radix.Label className={styles.heading}>{children}</Radix.Label>;
}

export function DropdownMenuSeparator() {
  return <Radix.Separator className={styles.separator} />;
}

export interface DropdownMenuRadioGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: ReactNode }>;
  label?: ReactNode;
}

/** A single choice inside a menu, e.g. the colour theme. */
export function DropdownMenuRadioGroup({
  value,
  onValueChange,
  options,
  label,
}: DropdownMenuRadioGroupProps) {
  return (
    <Radix.Group>
      {label ? <Radix.Label className={styles.heading}>{label}</Radix.Label> : null}
      <Radix.RadioGroup value={value} onValueChange={onValueChange}>
        {options.map((option) => (
          <Radix.RadioItem key={option.value} value={option.value} className={styles.item}>
            <span className={styles.check} aria-hidden="true">
              <Radix.ItemIndicator>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <path d="M3.5 8.5l3 3 6-7" />
                </svg>
              </Radix.ItemIndicator>
            </span>
            <span className={styles.label}>{option.label}</span>
          </Radix.RadioItem>
        ))}
      </Radix.RadioGroup>
    </Radix.Group>
  );
}
